import useSWR from 'swr';

import { fetchEndpointData } from '@utils/fetchEndpointData';
import {
  Country,
  CustomerDocumentType,
  CustomerType,
  InvoiceDeliveryMethod,
  InvoiceFrequency,
  Language,
  OrderDocumentType,
  PaymentTerm,
  TrailerType,
} from 'src/types/enums';

import type { CustomersResponse } from './endpoints/CustomersEndpoint.types';
import type { CustomerExportItem, CustomersForExportResponse } from './endpoints/CustomersForExportEndpoint.types';
import type { CustomerDetail, CustomerByIdResponse } from './endpoints/CustomerEndpoint.types';
import type {
  CustomerPortalCustomer,
  CustomerPortalCustomerByIdResponse,
} from './endpoints/CustomerPortalCustomerEndpoint.types';
import type { CustomerNameByInviteLinkResponse } from './endpoints/CustomerNameByInviteLinkEndpoint.types';
import type { CustomerSustainabilityReportingResponse } from './endpoints/CustomerSustainabilityReportingEndpoint.types';
import type { CreateCustomerResponse } from './endpoints/CreateCustomerEndpoint.types';
import type { UpdateCustomerResponse } from './endpoints/UpdateCustomerEndpoint.types';
import type { CustomerPortalUpdateCustomerResponse } from './endpoints/CustomerPortalUpdateCustomerEndpoint.types';
import type { CreateCustomerContactResponse } from './endpoints/CreateCustomerContactEndpoint.types';
import type { UpdateCustomerContactResponse } from './endpoints/UpdateCustomerContactEndpoint.types';
import type { DeleteCustomerContactsResponse } from './endpoints/DeleteCustomerContactsEndpoint.types';
import type { LinkCustomerDocumentResponse } from './endpoints/LinkCustomerDocumentEndpoint.types';
import type { UnlinkCustomerDocumentResponse } from './endpoints/UnlinkCustomerDocumentEndpoint.types';
import type { InviteCustomerUserResponse } from './endpoints/InviteCustomerUserEndpoint.types';
import type { DeactivateCustomerUserResponse } from './endpoints/DeactivateCustomerUserEndpoint.types';
import type { DeleteCustomerUserInviteResponse } from './endpoints/DeleteCustomerUserInviteEndpoint.types';
import type { AcceptCustomerUserInviteResponse } from './endpoints/AcceptCustomerUserInviteEndpoint.types';

// Cleaner re-exports for consumers.
export type Customer = CustomerDetail;
export type CustomerExportEntry = CustomerExportItem;
export type CustomerPortalCustomerEntry = CustomerPortalCustomer;

export const CUSTOMERS_SWR_KEY = 'customers';
export const CUSTOMER_SWR_KEY = 'customer';
export const CUSTOMER_PORTAL_CUSTOMER_SWR_KEY = 'customer-portal-customer';
export const CUSTOMER_NAME_BY_INVITE_LINK_SWR_KEY = 'customer-name-by-invite-link';
export const CUSTOMER_SUSTAINABILITY_SWR_KEY = 'customer-sustainability-reporting';

// ─── Queries ─────────────────────────────────────────────────────────

export interface CustomersFilters {
  search?: string;
  isActive?: boolean;
}

export async function getCustomers(
  cursor?: number | null,
  take?: number,
  filters?: CustomersFilters,
): Promise<CustomersResponse> {
  const params = new URLSearchParams();
  if (cursor != null) params.set('id', cursor.toString());
  if (take != null) params.set('take', take.toString());
  if (filters?.search) params.set('search', filters.search);
  if (filters?.isActive != null) params.set('isActive', filters.isActive ? 'true' : 'false');

  const queryString = params.toString();
  const url = queryString ? `/api/v1/customers?${queryString}` : '/api/v1/customers';
  return fetchEndpointData<CustomersResponse>(url);
}

export async function getCustomersForExport(
  cursor?: number | null,
  take?: number,
  filters?: CustomersFilters,
): Promise<CustomersForExportResponse> {
  const params = new URLSearchParams();
  if (cursor != null) params.set('id', cursor.toString());
  if (take != null) params.set('take', take.toString());
  if (filters?.search) params.set('search', filters.search);
  if (filters?.isActive != null) params.set('isActive', filters.isActive ? 'true' : 'false');

  const queryString = params.toString();
  const url = queryString ? `/api/v1/customers/export?${queryString}` : '/api/v1/customers/export';
  return fetchEndpointData<CustomersForExportResponse>(url);
}

export async function getCustomerById(id: number): Promise<CustomerByIdResponse> {
  return fetchEndpointData<CustomerByIdResponse>(`/api/v1/customers/${id}`);
}

export async function getCustomerPortalCustomerById(id: number): Promise<CustomerPortalCustomerByIdResponse> {
  return fetchEndpointData<CustomerPortalCustomerByIdResponse>(`/api/v1/customer-portal/customers/${id}`);
}

export async function getCustomerNameByInviteLink(
  customerId: number,
  email: string,
  token: string,
): Promise<CustomerNameByInviteLinkResponse> {
  const params = new URLSearchParams({ customerId: customerId.toString(), email, token });
  return fetchEndpointData<CustomerNameByInviteLinkResponse>(
    `/api/v1/customer-portal/customer-name-by-invite-link?${params.toString()}`,
  );
}

export interface SustainabilityReportFilter {
  startDate: string;
  endDate: string;
}

export async function getCustomerSustainabilityReporting(
  customerId: number,
  filter: SustainabilityReportFilter,
): Promise<CustomerSustainabilityReportingResponse> {
  const params = new URLSearchParams({ startDate: filter.startDate, endDate: filter.endDate });
  return fetchEndpointData<CustomerSustainabilityReportingResponse>(
    `/api/v1/customers/${customerId}/sustainability-reporting?${params.toString()}`,
  );
}

// ─── Query hooks ─────────────────────────────────────────────────────

// Mirrors CustomerComboboxField / the credit-note & incasso CustomerSelect
// `useGetCustomersQuery` (context suspense: false) — non-suspense, reads `data`
// defensively.
export function useCustomers(filters: CustomersFilters) {
  return useSWR([CUSTOMERS_SWR_KEY, filters.search, filters.isActive], () =>
    getCustomers(undefined, undefined, filters),
  );
}

// The internal customer page checks `isLoading` before falling back to a
// "Klant niet gevonden" error, so this stays non-suspense.
export function useCustomerById(id: number | null | undefined) {
  return useSWR(id != null ? [CUSTOMER_SWR_KEY, id] : null, () => getCustomerById(id!));
}

export function useCustomerPortalCustomerById(id: number) {
  return useSWR([CUSTOMER_PORTAL_CUSTOMER_SWR_KEY, id], () => getCustomerPortalCustomerById(id));
}

export function useCustomerNameByInviteLink(customerId: number, email: string, token: string) {
  return useSWR([CUSTOMER_NAME_BY_INVITE_LINK_SWR_KEY, customerId, email, token], () =>
    getCustomerNameByInviteLink(customerId, email, token),
  );
}

// The sustainability page paused the query until a time frame was selected, then
// read `data?.customerSustainabilityReporting` defensively — non-suspense with a
// null key when no time frame is set.
export function useCustomerSustainabilityReporting(customerId: number, filter: SustainabilityReportFilter | null) {
  return useSWR(filter ? [CUSTOMER_SUSTAINABILITY_SWR_KEY, customerId, filter.startDate, filter.endDate] : null, () =>
    getCustomerSustainabilityReporting(customerId, filter!),
  );
}

// ─── Mutations (fetchers throw on error) ─────────────────────────────

export interface CreateCustomerInput {
  accountingId?: string;
  name: string;
  type: CustomerType;
  vatNumber: string;
  companyNumber: string;
  glnNumber?: string;
  street: string;
  streetNumber: string;
  city: string;
  postalCode: string;
  country: Country;
  language: Language;
  paymentTerm: PaymentTerm;
  invoiceFrequency: InvoiceFrequency;
  invoiceDeliveryMethod: InvoiceDeliveryMethod;
  onlySendInvoiceAttachment?: boolean;
  mergeInvoicePdfs?: boolean;
  internalNotes?: string;
  externalNotes?: string;
  dealOwnerId: string;
  defaultCargoTypeId?: string;
  defaultVatRateId: string;
  sendCMRWithInvoice: boolean;
  autoInvoicing: boolean;
  dieselSurchargePercentage: number;
  dieselSurchargeEnabled: boolean;
  hasCustomDieselSurcharge: boolean;
  defaultTrailerTypes?: TrailerType[];
  cmrReminderOverwrite?: number | null;
  hasSelfBilling?: boolean;
  peppolEnabled?: boolean;
  emailEnabled?: boolean;
  cmrEmailSubjectOverwrite?: string;
  autoProcessCmr?: boolean;
  requiresPrepayment?: boolean;
  requiredDocuments?: OrderDocumentType[];
}

export async function createCustomer(data: CreateCustomerInput): Promise<CreateCustomerResponse> {
  return fetchEndpointData<CreateCustomerResponse>('/api/v1/customers', {
    method: 'POST',
    body: data,
  });
}

export interface UpdateCustomerInput {
  accountingId?: string;
  name?: string;
  dealOwnerId?: string;
  type?: CustomerType;
  vatNumber?: string;
  companyNumber?: string;
  glnNumber?: string;
  street?: string;
  streetNumber?: string;
  city?: string;
  postalCode?: string;
  country?: Country;
  language?: Language;
  paymentTerm?: PaymentTerm;
  invoiceFrequency?: InvoiceFrequency;
  invoiceDeliveryMethod?: InvoiceDeliveryMethod;
  onlySendInvoiceAttachment?: boolean;
  mergeInvoicePdfs?: boolean;
  internalNotes?: string;
  externalNotes?: string;
  deactivationReason?: string | null;
  autoInvoicing?: boolean;
  dieselSurchargeEnabled?: boolean;
  hasCustomDieselSurcharge?: boolean;
  dieselSurchargePercentage?: number;
  defaultCargoTypeId?: string;
  defaultVatRateId?: string;
  sendCMRWithInvoice?: boolean;
  defaultTrailerTypes?: TrailerType[];
  cmrReminderOverwrite?: number | null;
  hasSelfBilling?: boolean;
  peppolEnabled?: boolean;
  emailEnabled?: boolean;
  cmrEmailSubjectOverwrite?: string;
  autoProcessCmr?: boolean;
  requiredDocuments?: OrderDocumentType[];
  requiresNeutralCmr?: boolean;
  requiresPrepayment?: boolean;
}

export async function updateCustomer(id: number, data: UpdateCustomerInput): Promise<UpdateCustomerResponse> {
  return fetchEndpointData<UpdateCustomerResponse>(`/api/v1/customers/${id}`, {
    method: 'PATCH',
    body: data,
  });
}

export interface CustomerPortalUpdateCustomerInput {
  street?: string;
  streetNumber?: string;
  city?: string;
  postalCode?: string;
  country?: Country;
  language?: Language;
}

export async function customerPortalUpdateCustomer(
  id: number,
  data: CustomerPortalUpdateCustomerInput,
): Promise<CustomerPortalUpdateCustomerResponse> {
  return fetchEndpointData<CustomerPortalUpdateCustomerResponse>(`/api/v1/customer-portal/customers/${id}`, {
    method: 'PATCH',
    body: data,
  });
}

export interface CreateCustomerContactInput {
  name: string;
  email: string;
  phone: string;
  language: Language;
  customerId: number;
  function?: string;
  shouldReceiveInvoices: boolean;
  shouldReceiveOrderConfirmations: boolean;
  shouldReceiveCMR: boolean;
  shouldReceiveQuotations: boolean;
  shouldReceiveDieselSurcharge: boolean;
  shouldReceivePaymentReminder?: boolean;
  internalNotes?: string;
}

export async function createCustomerContact(data: CreateCustomerContactInput): Promise<CreateCustomerContactResponse> {
  return fetchEndpointData<CreateCustomerContactResponse>('/api/v1/customers/contacts', {
    method: 'POST',
    body: data,
  });
}

export interface UpdateCustomerContactInput {
  name?: string;
  email?: string;
  phone?: string;
  language?: Language;
  shouldReceiveInvoices?: boolean;
  shouldReceiveOrderConfirmations?: boolean;
  shouldReceiveCMR?: boolean;
  shouldReceiveQuotations?: boolean;
  shouldReceiveDieselSurcharge?: boolean;
  shouldReceivePaymentReminder?: boolean;
  internalNotes?: string;
  function?: string;
}

export async function updateCustomerContact(
  id: number,
  data: UpdateCustomerContactInput,
): Promise<UpdateCustomerContactResponse> {
  return fetchEndpointData<UpdateCustomerContactResponse>(`/api/v1/customers/contacts/${id}`, {
    method: 'PATCH',
    body: data,
  });
}

export async function deleteCustomerContacts(
  customerId: number,
  ids: number[],
): Promise<DeleteCustomerContactsResponse> {
  return fetchEndpointData<DeleteCustomerContactsResponse>('/api/v1/customers/contacts', {
    method: 'DELETE',
    body: { customerId, ids },
  });
}

export async function linkCustomerDocument(
  customerId: number,
  documentId: string,
  type: CustomerDocumentType,
): Promise<LinkCustomerDocumentResponse> {
  return fetchEndpointData<LinkCustomerDocumentResponse>(`/api/v1/customers/${customerId}/documents`, {
    method: 'POST',
    body: { documentId, type },
  });
}

export async function unlinkCustomerDocument(
  customerId: number,
  documentId: string,
  type: CustomerDocumentType,
): Promise<UnlinkCustomerDocumentResponse> {
  return fetchEndpointData<UnlinkCustomerDocumentResponse>(`/api/v1/customers/${customerId}/documents`, {
    method: 'DELETE',
    body: { documentId, type },
  });
}

export async function inviteCustomerUser(customerId: number, email: string): Promise<InviteCustomerUserResponse> {
  return fetchEndpointData<InviteCustomerUserResponse>(`/api/v1/customers/${customerId}/users/invite`, {
    method: 'POST',
    body: { email },
  });
}

export async function deactivateCustomerUser(
  customerId: number,
  userId: string,
): Promise<DeactivateCustomerUserResponse> {
  return fetchEndpointData<DeactivateCustomerUserResponse>(`/api/v1/customers/${customerId}/users/deactivate`, {
    method: 'POST',
    body: { userId },
  });
}

export async function deleteCustomerUserInvite(
  customerId: number,
  inviteId: string,
): Promise<DeleteCustomerUserInviteResponse> {
  return fetchEndpointData<DeleteCustomerUserInviteResponse>(
    `/api/v1/customers/${customerId}/users/invites/${inviteId}`,
    {
      method: 'DELETE',
    },
  );
}

export async function acceptCustomerUserInvite(data: {
  customerId: number;
  email: string;
  token: string;
  password: string;
}): Promise<AcceptCustomerUserInviteResponse> {
  return fetchEndpointData<AcceptCustomerUserInviteResponse>('/api/v1/customer-portal/accept-invite', {
    method: 'POST',
    body: data,
  });
}
